import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useScrollTrigger,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';

const navItems = [
  { label: 'Home', path: '/' },
  { label: 'About', path: '/about' },
  { label: 'News', path: '/news' },
];

function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 50,
  });

  const isHome = location.pathname === '/';
  const isTransparent = isHome && !trigger;

  return (
    <>
      <AppBar 
        position="fixed" 
        elevation={isTransparent ? 0 : 1}
        sx={{ 
          background: isTransparent 
            ? 'transparent' 
            : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: isTransparent ? 'none' : 'blur(10px)',
          transition: 'all 0.3s ease',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar sx={{ py: 1 }}>
            <Typography
              component={Link}
              to="/"
              variant="h6"
              sx={{
                flexGrow: 1,
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: 800,
                fontSize: '1.3rem',
                color: isTransparent ? '#fff' : 'primary.main',
                textDecoration: 'none',
                letterSpacing: '-0.01em',
              }}
            >
              TEXAS HIGHWAY RANGERS
            </Typography>

            {isMobile ? (
              <IconButton
                edge="end"
                onClick={() => setMobileOpen(true)}
                sx={{ color: isTransparent ? '#fff' : 'primary.main' }}
              >
                <MenuIcon />
              </IconButton>
            ) : (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {navItems.map((item) => (
                  <Button
                    key={item.path}
                    component={Link}
                    to={item.path}
                    sx={{
                      color: isTransparent ? '#fff' : 'text.primary',
                      fontWeight: location.pathname === item.path ? 700 : 500,
                      '&:hover': {
                        background: isTransparent 
                          ? 'rgba(255,255,255,0.1)' 
                          : 'rgba(26, 35, 126, 0.08)',
                      },
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
              </Box>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{
          sx: { width: '100%', maxWidth: 320 },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton onClick={() => setMobileOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <List sx={{ px: 2 }}>
          {navItems.map((item) => (
            <ListItem
              key={item.path}
              component={Link}
              to={item.path}
              onClick={() => setMobileOpen(false)}
              sx={{
                borderRadius: 2,
                mb: 1,
                bgcolor: location.pathname === item.path ? 'primary.main' : 'transparent',
                color: location.pathname === item.path ? '#fff' : 'text.primary',
                '&:hover': {
                  bgcolor: location.pathname === item.path ? 'primary.dark' : 'grey.100',
                },
              }}
            >
              <ListItemText 
                primary={item.label} 
                primaryTypographyProps={{ fontWeight: 600 }}
              />
            </ListItem>
          ))}
        </List>
      </Drawer>

      {/* Spacer for fixed navbar (only on non-home pages) */}
      {!isHome && <Toolbar sx={{ mb: 2 }} />}
    </>
  );
}

export default Navbar;
